import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { apiGet, apiPatch } from "../lib/api";

interface AdConfig {
  adsEnabled: boolean;
  testUserIds: string[];
  updatedAt: string;
  updatedBy: string | null;
}

export default function AdminAdsSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [config, setConfig] = useState<AdConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Test user management
  const [newTestUserId, setNewTestUserId] = useState("");

  const canUse = !!user?.isAdmin;

  useEffect(() => {
    if (!canUse) {
      navigate("/");
      return;
    }
    loadConfig();
  }, [canUse, navigate]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiGet<{
        ok: boolean;
        config: AdConfig;
      }>("/api/admin/ad-config");

      if (data.ok) {
        setConfig(data.config);
      }
    } catch (err: any) {
      console.error("Ad config fetch error:", err);
      setError(err.message || "Reklam ayarları yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const toggleAds = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const newStatus = !config.adsEnabled;

      const data = await apiPatch<{
        ok: boolean;
        config: AdConfig;
      }>("/api/admin/ad-config", {
        adsEnabled: newStatus,
      });

      if (data.ok) {
        setConfig(data.config);
        setSuccessMessage(
          newStatus
            ? "Reklamlar başarıyla AÇILDI"
            : "Reklamlar başarıyla KAPATILDI"
        );

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      console.error("Ad toggle error:", err);
      setError(err.message || "Reklam ayarları güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  const addTestUser = async () => {
    if (!config || !newTestUserId.trim()) return;

    // Prevent duplicates
    if (config.testUserIds.includes(newTestUserId.trim())) {
      setError("Bu kullanıcı zaten test listesinde");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const updatedList = [...config.testUserIds, newTestUserId.trim()];

      const data = await apiPatch<{
        ok: boolean;
        config: AdConfig;
      }>("/api/admin/ad-config", {
        testUserIds: updatedList,
      });

      if (data.ok) {
        setConfig(data.config);
        setNewTestUserId("");
        setSuccessMessage("Test kullanıcı eklendi");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      console.error("Add test user error:", err);
      setError(err.message || "Test kullanıcı eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  const removeTestUser = async (userId: string) => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const updatedList = config.testUserIds.filter((id) => id !== userId);

      const data = await apiPatch<{
        ok: boolean;
        config: AdConfig;
      }>("/api/admin/ad-config", {
        testUserIds: updatedList,
      });

      if (data.ok) {
        setConfig(data.config);
        setSuccessMessage("Test kullanıcı silindi");
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      console.error("Remove test user error:", err);
      setError(err.message || "Test kullanıcı silinemedi");
    } finally {
      setSaving(false);
    }
  };

  if (!canUse) {
    return null;
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.loadingText}>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.errorText}>Ayarlar yüklenemedi</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate("/admin/dashboard")} style={styles.backButton}>
          ← Geri
        </button>
        <h1 style={styles.title}>Reklam Ayarları</h1>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div style={styles.errorBox}>
          <p style={styles.errorText}>{error}</p>
        </div>
      )}

      {successMessage && (
        <div style={styles.successBox}>
          <p style={styles.successText}>{successMessage}</p>
        </div>
      )}

      {/* Main Settings Card */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Global Reklam Kontrolü</h2>

        <div style={styles.statusSection}>
          <div style={styles.statusLabel}>
            <strong>Mevcut Durum:</strong>
          </div>
          <div
            style={{
              ...styles.statusBadge,
              backgroundColor: config.adsEnabled ? "#10B981" : "#EF4444",
            }}
          >
            {config.adsEnabled ? "Reklamlar AÇIK" : "Reklamlar KAPALI"}
          </div>
        </div>

        <div style={styles.buttonContainer}>
          <button
            onClick={toggleAds}
            disabled={saving}
            style={{
              ...styles.toggleButton,
              backgroundColor: config.adsEnabled ? "#EF4444" : "#10B981",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving
              ? "Kaydediliyor..."
              : config.adsEnabled
              ? "Reklamları Kapat"
              : "Reklamları Aç"}
          </button>
        </div>

        <div style={styles.infoBox}>
          <p style={styles.infoText}>
            <strong>Açıklama:</strong>
          </p>
          <ul style={styles.infoList}>
            <li>
              Reklamlar <strong>AÇIK</strong> olduğunda: Tüm kullanıcılar
              reklamları görür.
            </li>
            <li>
              Reklamlar <strong>KAPALI</strong> olduğunda: Sadece test
              kullanıcıları reklamları görür.
            </li>
          </ul>
        </div>

        {config.updatedAt && (
          <p style={styles.updatedText}>
            Son güncelleme: {new Date(config.updatedAt).toLocaleString("tr-TR")}
          </p>
        )}
      </div>

      {/* Test Users Section */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Test Kullanıcıları</h2>

        <p style={styles.description}>
          Test kullanıcıları, reklamlar kapalı olsa bile reklamları görebilir.
          Bu özellik reklam testleri için kullanışlıdır.
        </p>

        {/* Add Test User */}
        <div style={styles.addTestUserSection}>
          <input
            type="text"
            placeholder="Kullanıcı ID'si"
            value={newTestUserId}
            onChange={(e) => setNewTestUserId(e.target.value)}
            style={styles.input}
            disabled={saving}
          />
          <button
            onClick={addTestUser}
            disabled={saving || !newTestUserId.trim()}
            style={{
              ...styles.addButton,
              cursor:
                saving || !newTestUserId.trim() ? "not-allowed" : "pointer",
              opacity: saving || !newTestUserId.trim() ? 0.6 : 1,
            }}
          >
            Ekle
          </button>
        </div>

        {/* Test Users List */}
        {config.testUserIds.length > 0 ? (
          <div style={styles.testUsersList}>
            <p style={styles.testUsersCount}>
              <strong>Toplam: {config.testUserIds.length} kullanıcı</strong>
            </p>
            {config.testUserIds.map((userId) => (
              <div key={userId} style={styles.testUserItem}>
                <span style={styles.testUserId}>{userId}</span>
                <button
                  onClick={() => removeTestUser(userId)}
                  disabled={saving}
                  style={{
                    ...styles.removeButton,
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  Sil
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p style={styles.emptyText}>Henüz test kullanıcısı eklenmemiş.</p>
        )}
      </div>
    </div>
  );
}

// Styles
const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "20px",
    minHeight: "100vh",
    backgroundColor: "#000",
    color: "#fff",
  },
  header: {
    marginBottom: "24px",
  },
  backButton: {
    background: "transparent",
    border: "1px solid #114232",
    color: "#114232",
    padding: "8px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    marginBottom: "12px",
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#114232",
    marginBottom: "8px",
  },
  card: {
    backgroundColor: "#111",
    border: "1px solid #222",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#fff",
    marginBottom: "16px",
  },
  statusSection: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
  },
  statusLabel: {
    fontSize: "16px",
    color: "#ccc",
  },
  statusBadge: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#fff",
  },
  buttonContainer: {
    marginBottom: "20px",
  },
  toggleButton: {
    width: "100%",
    padding: "14px",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#fff",
    cursor: "pointer",
    transition: "opacity 0.2s",
  },
  infoBox: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
  },
  infoText: {
    fontSize: "14px",
    color: "#ccc",
    marginBottom: "8px",
  },
  infoList: {
    fontSize: "14px",
    color: "#aaa",
    paddingLeft: "20px",
    margin: "8px 0",
  },
  updatedText: {
    fontSize: "12px",
    color: "#666",
    marginTop: "16px",
  },
  description: {
    fontSize: "14px",
    color: "#aaa",
    marginBottom: "20px",
  },
  addTestUserSection: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
  },
  input: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    color: "#fff",
    fontSize: "14px",
  },
  addButton: {
    padding: "12px 24px",
    backgroundColor: "#114232",
    border: "none",
    borderRadius: "8px",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  testUsersList: {
    marginTop: "16px",
  },
  testUsersCount: {
    fontSize: "14px",
    color: "#ccc",
    marginBottom: "12px",
  },
  testUserItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px",
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    marginBottom: "8px",
  },
  testUserId: {
    fontSize: "14px",
    color: "#fff",
    fontFamily: "monospace",
  },
  removeButton: {
    padding: "6px 12px",
    backgroundColor: "#EF4444",
    border: "none",
    borderRadius: "6px",
    color: "#fff",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  },
  emptyText: {
    fontSize: "14px",
    color: "#666",
    textAlign: "center",
    padding: "20px",
  },
  loadingText: {
    fontSize: "16px",
    color: "#ccc",
    textAlign: "center",
  },
  errorBox: {
    backgroundColor: "#3F1E1E",
    border: "1px solid #EF4444",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
  },
  errorText: {
    color: "#EF4444",
    fontSize: "14px",
    margin: 0,
  },
  successBox: {
    backgroundColor: "#1E3F2E",
    border: "1px solid #10B981",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
  },
  successText: {
    color: "#10B981",
    fontSize: "14px",
    margin: 0,
  },
};
